// My canvas areas — home widget (Oct 19, 2026).
//
// Greg: "if a team member has been assigned to areas, have a widget on their
// home page showing those so they can see city/zip, pins, activity at a
// glance." So it renders NOTHING for someone with no active area (most of the
// team on any given day) — no empty card — and appears as soon as a manager
// puts them on one (realtime on canvas_area_assignments).
//
// Every number comes from canvas_area_stats(p_user = this person): pins are
// the knock pins inside the polygon right now; "your" numbers are this
// person's visits in the area; the sparkline is everyone's knocks there.
// Read-only — tapping an area opens the canvas map fitted to it.

import { Link } from "react-router-dom";
import { logCardClick } from "../../../lib/uiEvents";
import {
  areaMapHref, areaName, areaPlace, doorsLeft, firstName, fmtShortDate, relTime, useAreaStats,
  type AreaStats,
} from "../../../lib/canvasAreas";
import { CoverageMeter, HailChip, KnockSparkline, PinStateBar, PinStateLegend, Stat } from "../../canvas/areas/AreaBits";
import { areaDayState, sortAreasForDay } from "../../../lib/dailyAreas";

const SHOW = 4;
const todayEastern = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

export default function MyAreasWidget({ memberId }: { memberId: string }) {
  const { areas, error } = useAreaStats({ userId: memberId });

  // Quiet until we know — most people have no area and must never see a
  // loading card flash and vanish.
  if (!areas && !error) return null;

  if (error && !areas) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-1 flex items-center gap-2">
          <span>📐</span>
          <span className="text-sm font-bold text-bardin-ink">My canvas areas</span>
        </div>
        <div className="text-xs text-slate-500">
          Couldn't load your areas right now —{" "}
          <Link to="/canvas" className="font-bold text-bardin-orange">open the canvas map</Link> instead.
        </div>
      </div>
    );
  }

  if (!areas || areas.length === 0) return null;

  const totalPins = areas.reduce((s, a) => s + a.pin_count, 0);
  const totalDoors = areas.reduce((s, a) => s + doorsLeft(a.states), 0);
  // Today's daily area first, then older ones still open, then standing areas.
  const shown = sortAreasForDay(areas, todayEastern()).slice(0, SHOW);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span>📐</span>
        <span className="whitespace-nowrap text-sm font-bold text-bardin-ink">My canvas areas</span>
        <span className="whitespace-nowrap rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-bardin-orange">
          {areas.length} {areas.length === 1 ? "area" : "areas"}
        </span>
        <span className="w-full text-xs font-bold text-slate-500 sm:ml-auto sm:w-auto">
          {totalDoors.toLocaleString()} doors left · {totalPins.toLocaleString()} pins
        </span>
      </div>
      <div className="mb-3 text-[11px] text-slate-500">
        Where you're assigned to knock — live. Your numbers are your knocks in the area; the bars are the whole team's.
      </div>

      {/* grid-cols-1 = minmax(0, 1fr): an area shared by many reps has a long
          "with …" line, and an auto column would grow to its full width. */}
      <div className={`grid grid-cols-1 gap-3 ${shown.length > 1 ? "lg:grid-cols-2" : ""}`}>
        {shown.map((a) => <AreaCard key={a.area_group_id} area={a} memberId={memberId} />)}
      </div>
      {areas.length > SHOW && (
        <div className="mt-2 text-[11px] text-slate-500">+{areas.length - SHOW} more on the map</div>
      )}

      <div className="mt-3 flex items-center border-t border-slate-100 pt-2.5">
        <Link to="/canvas" onClick={() => logCardClick("widget_myareas_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange">
          Open the canvas map ›
        </Link>
      </div>
    </div>
  );
}

function AreaCard({ area, memberId }: { area: AreaStats; memberId: string }) {
  const me = area.reps.find((r) => r.user_id === memberId) ?? null;
  const teammates = area.reps.filter((r) => r.user_id !== memberId);
  const place = areaPlace(area);
  const name = areaName(area);
  const act = area.activity;

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-[14px] font-extrabold text-bardin-ink">{name}</div>
            {area.for_date && areaDayState(area, todayEastern()) === "today" && (
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-800">Today</span>
            )}
            {area.for_date && areaDayState(area, todayEastern()) !== "today" && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">For {fmtShortDate(area.for_date)}</span>
            )}
          </div>
          <div className="truncate text-[11px] text-slate-500">
            {[
              name !== place && place ? `📍 ${place}` : null,
              area.anchor && (area.anchor.street || area.anchor.city) ? `around ${[area.anchor.street, area.anchor.city].filter(Boolean).join(", ")}` : null,
              area.sq_mi > 0 ? `${area.sq_mi} sq mi` : null,
              teammates.length > 0 ? `with ${teammates.map((t) => firstName(t.full_name)).join(", ")}` : null,
            ].filter(Boolean).join(" · ")}
          </div>
        </div>
        <HailChip hail={area.hail} />
      </div>

      <div className="mt-2.5">
        <PinStateBar states={area.states} total={area.pin_count} />
        <div className="mt-1.5"><PinStateLegend states={area.states} /></div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        <Stat label="Doors left" value={doorsLeft(area.states)} tone="text-bardin-orange"
          hint="Pins never knocked, or knocked with no answer yet" />
        <Stat label="You today" value={me?.knocks_today ?? 0} hint="Your knocks in this area today" />
        <Stat label="You 7 days" value={me?.knocks_7d ?? 0} hint="Your knocks in this area, last 7 days" />
        <Stat label="Talked 7d" value={me?.talked_7d ?? 0} tone="text-purple-700"
          hint="Doors where you talked to someone in this area, last 7 days" />
      </div>

      <div className="mt-3 flex items-end gap-3">
        <KnockSparkline daily={area.daily} />
        <div className="min-w-0 flex-1 text-[11px] leading-snug text-slate-500">
          <div>
            Team: <b className="tabular-nums text-slate-700">{act.knocks_7d}</b> knocks ·{" "}
            <b className="tabular-nums text-slate-700">{act.appts_7d}</b> appts this week
          </div>
          <div>
            Last knock {relTime(act.last_knock_at)}
            {act.last_knock_by_name
              ? ` · ${me && act.last_knock_by_name === me.full_name ? "you" : firstName(act.last_knock_by_name)}`
              : ""}
          </div>
        </div>
        <Link to={areaMapHref(area.area_group_id)} onClick={() => logCardClick("widget_myareas_map")}
          className="flex-none rounded-lg bg-orange-50 px-2.5 py-1.5 text-[11px] font-extrabold text-bardin-orange hover:bg-orange-100">
          🗺 Map
        </Link>
      </div>

      <div className="mt-3"><CoverageMeter area={area} /></div>
    </div>
  );
}
